// ClubsPage.js — /clubs: a tile for every club that has played in the
// Premier League (crest, name, seasons), with a search box and an All / this
// season / Former filter. Each tile opens that club's page. The combined
// records live on /all-time-table.

import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Skeleton, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { useSeededQuery } from "./components/scorelines/data";
import { ClubSearch, StatusFilter } from "./components/clubs/ClubFilters";
import { clubInfo, clubPath, seasonIdStart, seasonText } from "./components/clubs/data";

const SITE_URL = "https://premierleaguescorigami.com";
const sortName = (t) => clubInfo(t.team).name.replace(/^AFC /, ""); // "AFC Bournemouth" files under B

export default function ClubsPage() {
  const teams = useSeededQuery("teams=1");
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");

  const data = useMemo(() => {
    const items = teams.data?.items;
    if (!items) return null;
    const current = Math.max(...items.map((t) => seasonIdStart(t.lastSeasonId)));
    const maxSeasons = Math.max(...items.map((t) => t.seasons));
    const isCurrent = (t) => seasonIdStart(t.lastSeasonId) === current;
    return {
      items: [...items].sort((a, b) => sortName(a).localeCompare(sortName(b))),
      current,
      isCurrent,
      counts: { all: items.length, current: items.filter(isCurrent).length, former: items.filter((t) => !isCurrent(t)).length },
      everPresent: items.filter((t) => t.seasons === maxSeasons).sort((a, b) => sortName(a).localeCompare(sortName(b))),
    };
  }, [teams.data]);

  const clubs = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.items
      .filter((t) => (status === "all" ? true : status === "current" ? data.isCurrent(t) : !data.isCurrent(t)))
      .filter((t) => !q || clubInfo(t.team).name.toLowerCase().includes(q) || clubInfo(t.team).short.toLowerCase().includes(q));
  }, [data, status, query]);

  const lede = data
    ? `${data.counts.all} clubs have played in the Premier League since it began in 1992, ${data.counts.current} of them in ${seasonText(data.current)}. ${data.everPresent.length} have been there for every season: ${data.everPresent
        .map((t) => clubInfo(t.team).short)
        .join(", ")
        .replace(/, ([^,]*)$/, " and $1")}.`
    : "Every club that has played in the Premier League since 1992.";

  return (
    <ProsePage wide>
      <SEO
        title="Every Premier League club since 1992"
        description="Every club that has played in the Premier League since 1992: each club's record, biggest wins and defeats, scoreline grid and season-by-season results."
        path="/clubs"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Clubs", item: `${SITE_URL}/clubs` },
          ],
        }}
      />
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader eyebrow="Clubs" title="Every Premier League club" lede={lede} />
        <P>
          Pick a club for its record, its own scoreline grid, its biggest wins and defeats, and its season-by-season
          results. Every club's combined record is in the <InternalLink to="/all-time-table">all-time table</InternalLink>.
        </P>
      </Box>

      {teams.error ? (
        <P>The clubs couldn't be loaded right now. Please try again shortly.</P>
      ) : !data ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 1.5 }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={148} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 3 }} />
          ))}
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <StatusFilter value={status} onChange={setStatus} counts={data.counts} currentLabel={`In ${seasonText(data.current)}`} />
            <ClubSearch id="club-search" value={query} onChange={setQuery} width={260} />
          </Box>

          <Box
            component="ul"
            aria-label="Premier League clubs"
            sx={{
              listStyle: "none",
              p: 0,
              m: 0,
              mb: 3,
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(auto-fill, minmax(160px, 1fr))" },
              gap: { xs: 1.25, sm: 1.5 },
            }}
          >
            {clubs.map((t) => (
              <li key={t.team}>
                <ClubTile club={t} current={data.isCurrent(t)} />
              </li>
            ))}
          </Box>
          {clubs.length === 0 && <P>No clubs match "{query}".</P>}

          <AdSlot position="records" />
        </>
      )}

      <CtaRow
        links={[
          { to: "/all-time-table", label: "All-time table", primary: true },
          { to: "/", label: "Explore the grid" },
          { to: "/scorelines", label: "Every scoreline" },
        ]}
      />
    </ProsePage>
  );
}

function ClubTile({ club: t, current }) {
  const info = clubInfo(t.team);
  const first = Number(t.firstDate.slice(0, 4)) - (Number(t.firstDate.slice(5, 7)) < 8 ? 1 : 0);
  return (
    <Box
      component={RouterLink}
      to={clubPath(t.team)}
      aria-label={`${info.name}: ${t.seasons} Premier League season${t.seasons === 1 ? "" : "s"}`}
      sx={{
        position: "relative",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
        px: 1.5,
        pt: 2.5,
        pb: 2,
        borderRadius: 3,
        textDecoration: "none",
        background: "linear-gradient(180deg, rgba(25,25,32,0.85) 0%, rgba(18,18,24,0.85) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
        transition: "transform 140ms ease-out, border-color 140ms ease-out, box-shadow 140ms ease-out",
        "&:hover, &:focus-visible": {
          transform: "translateY(-2px)",
          borderColor: BRAND.cyan,
          boxShadow: "0 8px 24px rgba(57,208,255,0.12)",
          outline: "none",
        },
      }}
    >
      {current && (
        <Box
          component="span"
          sx={{ position: "absolute", top: 8, right: 10, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: BRAND.cyan }}
          title={`In the Premier League in ${seasonText(seasonIdStart(t.lastSeasonId))}`}
        >
          {seasonText(seasonIdStart(t.lastSeasonId))}
        </Box>
      )}
      <Box
        sx={{
          width: 68,
          height: 68,
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 70%)",
        }}
      >
        {t.image && <Box component="img" src={t.image} alt="" loading="lazy" sx={{ width: 54, height: 54, objectFit: "contain" }} />}
      </Box>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14.5, lineHeight: 1.25 }}>{info.name}</Typography>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 12, fontFeatureSettings: '"tnum"' }}>
        {t.seasons} season{t.seasons === 1 ? "" : "s"} · since {seasonText(first)}
      </Typography>
    </Box>
  );
}
